export const POSTS = [
  {
    slug: "shipping-ai-agents-that-actually-work",
    title: "Shipping AI agents that actually work in production",
    excerpt:
      "Most agentic demos fall apart the moment a real user touches them. Here's the eval-driven loop we run at Ulties to keep ours honest.",
    author: "Mira Kahn",
    role: "Principal · Engineering",
    date: "2025-11-12",
    readTime: "9 min",
    tag: "AI",
    cover:
      "https://images.unsplash.com/photo-1709625862266-014ef072fd93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJ5JTIwZGFyayUyMDNkfGVufDB8fHx8MTc4MTM2ODEzM3ww&ixlib=rb-4.1.0&q=85",
    paragraphs: [
      "Agent demos look magical because the demo path is hand-tuned. Production is the opposite — users do strange things, latency spikes, models drift, and tools fail. Without a brutal evaluation loop, you ship a parlor trick.",
      "At Ulties, we treat agents like distributed systems. Every agent step is observable, replayable, and graded by a deterministic + LLM-judge hybrid. We run regression suites every commit and refuse to ship if the suite drops more than 1.5%.",
      "The shortest version: define what good looks like before you write a single prompt. Build a golden set of 200–1,000 historical cases. Score every change against it. Stop tuning prompts in the dark.",
      "If you do this for two weeks, your agent will go from 'cool demo' to something users actually rely on. If you don't, you'll be patching prompts at 3am for the next year.",
    ],
  },
  {
    slug: "the-case-against-microservices-for-startups",
    title: "The case against microservices for startups under 30 engineers",
    excerpt:
      "Conway's law is real, but so is your runway. A pragmatic look at when monoliths win — and when they don't.",
    author: "Daniel Okafor",
    role: "Principal · AI Systems",
    date: "2025-10-04",
    readTime: "7 min",
    tag: "Engineering",
    cover:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9kZSUyMGRhc2hib2FyZCUyMHVpfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85",
    paragraphs: [
      "Microservices buy you organisational scaling. They cost you everything else.",
      "Before you have 30 engineers and clear domain ownership, splitting your system is paying a tax for a benefit you don't yet need. Monoliths give you transactions, fewer moving parts, and faster refactors.",
      "The signal that you've outgrown a monolith isn't 'we feel slow'. It's two specific teams stepping on each other in the same module every sprint. When that pattern repeats for a quarter, then carve a service out — not before.",
    ],
  },
  {
    slug: "zero-trust-for-the-impatient",
    title: "Zero-trust security for the impatient",
    excerpt:
      "How we collapsed a flat corporate VPN into an identity-aware mesh across 2,400 endpoints in nine weeks.",
    author: "Mira Kahn",
    role: "Principal · Engineering",
    date: "2025-08-21",
    readTime: "11 min",
    tag: "Security",
    cover:
      "https://images.unsplash.com/photo-1751448555253-f39c06e29d82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxjeWJlciUyMHNlY3VyaXR5JTIwYWJzdHJhY3QlMjBkYXJrfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85",
    paragraphs: [
      "Zero-trust isn't a product, it's a posture. The shortest path is to identify every trust boundary, place an identity-aware proxy at each one, and remove every implicit trust your network currently grants.",
      "You don't need to boil the ocean. Start with the riskiest boundary — usually contractor laptops or third-party SaaS — and harden it end to end before moving on.",
      "Tooling matters less than discipline. SPIFFE, Tailscale, Envoy, and Cloudflare Access can all get you there. Pick one and commit.",
    ],
  },
  {
    slug: "the-design-systems-trap",
    title: "The design systems trap (and how to avoid it)",
    excerpt:
      "Design systems become museums. Here's the operating model that keeps them as living engineering tools.",
    author: "Mira Kahn",
    role: "Principal · Engineering",
    date: "2025-06-30",
    readTime: "6 min",
    tag: "Product",
    cover:
      "https://images.unsplash.com/photo-1770894807442-108cc33c0a7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxwb3J0cmFpdCUyMGRhcmslMjBiYWNrZ3JvdW5kJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85",
    paragraphs: [
      "Design systems fail when they become a separate product. The cure is to make them a thin layer over your actual components, not a brand bible.",
      "Ship the system in the same repo, version it with everything else, and never let it block product work. If a one-off needs a custom variant, that's fine — promote it later when a second product needs it.",
    ],
  },
  {
    slug: "what-we-learned-shipping-47-products",
    title: "What we learned shipping 47 products",
    excerpt:
      "Six years, four continents, one consistent finding: the hard part is never the technology.",
    author: "Daniel Okafor",
    role: "Principal · AI Systems",
    date: "2025-04-15",
    readTime: "8 min",
    tag: "Studio",
    cover:
      "https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwb3J0cmFpdCUyMGRhcmslMjBiYWNrZ3JvdW5kJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc4MTM2ODEyN3ww&ixlib=rb-4.1.0&q=85",
    paragraphs: [
      "Every successful project we've shipped had three things in common: a single accountable owner on the client side, a weekly demo cadence, and a written definition of done.",
      "Every project that struggled had the opposite: vague ownership, monthly stakeholder reviews, and a moving target.",
      "The technology is rarely the bottleneck. Operating model is.",
    ],
  },
  {
    slug: "what-good-engineering-looks-like-in-2026",
    title: "What good engineering looks like in 2026",
    excerpt:
      "Type-safety. Observable from day one. Agentic-tested. The new floor isn't where you think.",
    author: "Mira Kahn",
    role: "Principal · Engineering",
    date: "2026-01-08",
    readTime: "5 min",
    tag: "Engineering",
    cover:
      "https://images.unsplash.com/photo-1709625862266-014ef072fd93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJ5JTIwZGFyayUyMDNkfGVufDB8fHx8MTc4MTM2ODEzM3ww&ixlib=rb-4.1.0&q=85",
    paragraphs: [
      "The floor has moved. In 2026 you can't ship a serious product without end-to-end type safety, structured logging, and agent-driven test generation. Anything less and you're paying interest on day one.",
      "The teams shipping fastest aren't the ones with the most engineers — they're the ones with the tightest feedback loops. That hasn't changed. What has changed is what 'tight' means.",
    ],
  },
];

export const POST_BY_SLUG = Object.fromEntries(POSTS.map((p) => [p.slug, p]));
